/**
 * /performance/rollup — HR roll-up of finalized outcomes (ADR-0013 Phase 4 /
 * D-5, D-9). Outcomes by manager and department, plus the acknowledgment tally
 * (acknowledged / awaiting / waived) from Phase 3.1 so HR can chase the
 * outstanding ones — and waive acknowledgment for a departed/unavailable
 * employee. Read-only reporting; calibration adjustment is deferred (D-9).
 */
import { createRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { listEmployees } from '../api/employees.js';
import { getRollup, listCycles, waiveAcknowledgment, type PerfOutcome } from '../api/performance.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { AckBadge } from './myPerformance.js';

export const performanceRollupRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/performance/rollup',
  component: PerformanceRollupPage,
});

function PerformanceRollupPage(): JSX.Element {
  const cycles = useQuery({ queryKey: ['perf', 'cycles'], queryFn: () => listCycles() });
  const [cycleId, setCycleId] = useState('');
  const active = cycleId || cycles.data?.cycles[0]?.id || '';
  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Performance roll-up</h1>
        <p className="text-sm text-gray-400">Finalized outcomes by manager and department, with acknowledgment status.</p>
      </header>
      {cycles.isLoading ? <Muted>Loading…</Muted> : (cycles.data?.cycles ?? []).length === 0 ? <Muted>No cycles yet.</Muted> : (
        <>
          <label className="block space-y-1 max-w-sm">
            <span className="text-xs text-gray-400">Cycle</span>
            <select value={active} onChange={(e) => setCycleId(e.target.value)} className={inputCls}>
              {(cycles.data?.cycles ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {active ? <Rollup cycleId={active} /> : null}
        </>
      )}
    </section>
  );
}

function Rollup({ cycleId }: { cycleId: string }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['perf', 'rollup', cycleId], queryFn: () => getRollup(cycleId) });
  const employeesQ = useQuery({ queryKey: ['employees', 'all'], queryFn: () => listEmployees({ limit: 200 }) });
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employeesQ.data?.employees ?? []) m.set(e.id, `${e.firstName} ${e.lastName}`);
    return (id: string | null): string => (id ? m.get(id) ?? id : '—');
  }, [employeesQ.data]);
  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['perf', 'rollup', cycleId] });

  if (q.isLoading) return <Muted>Loading…</Muted>;
  if (q.isError || !q.data) return <Alert variant="error">Could not load the roll-up.</Alert>;
  const { outcomes, summary } = q.data;
  if (summary.count === 0) return <Muted>No finalized outcomes in this cycle yet.</Muted>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <Tally label="Acknowledged" value={summary.acknowledgment.acknowledged} tone="green" />
        <Tally label="Awaiting" value={summary.acknowledgment.awaiting} tone="yellow" />
        <Tally label="Waived" value={summary.acknowledgment.waived} tone="gray" />
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-gray-500">Outcomes ({summary.count})</p>
        {outcomes.map((o) => <OutcomeRow key={o.id} outcome={o} nameOf={nameOf} onWaived={() => void refresh()} />)}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <AvgTable title="By manager" rows={summary.byManager.map((r) => ({ ...r, label: nameOf(r.group === '(unassigned)' ? null : r.group) }))} />
        <AvgTable title="By department" rows={summary.byDepartment.map((r) => ({ ...r, label: r.group }))} />
      </div>
    </div>
  );
}

function OutcomeRow({ outcome, nameOf, onWaived }: { outcome: PerfOutcome; nameOf: (id: string | null) => string; onWaived: () => void }): JSX.Element {
  const [waiving, setWaiving] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => waiveAcknowledgment(outcome.id, reason),
    onSuccess: () => { setWaiving(false); setReason(''); onWaived(); },
  });
  const name = outcome.subject ? `${outcome.subject.firstName} ${outcome.subject.lastName}` : nameOf(outcome.subjectEmployeeId);
  return (
    <div className="rounded border border-gray-900 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-white">{name}{outcome.subject?.department ? <span className="text-gray-500 text-xs ml-2">{outcome.subject.department}</span> : null}</p>
          <p className="text-xs text-gray-500 mt-0.5">final {Number(outcome.finalScore)} · KRA {Number(outcome.kraScore)} · competency {Number(outcome.competencyScore)}</p>
        </div>
        <div className="flex items-center gap-3">
          <AckBadge status={outcome.acknowledgmentStatus} />
          {outcome.acknowledgmentStatus === 'awaiting' ? (
            <button type="button" onClick={() => setWaiving((v) => !v)} className={secondaryBtn}>Waive</button>
          ) : null}
        </div>
      </div>
      {waiving ? (
        <div className="space-y-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className={`${inputCls} w-full`} />
          <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending || !reason.trim()} className={primaryBtn}>{mut.isPending ? 'Waiving…' : 'Confirm waive'}</button>
          <ApiErrorNotice error={mut.error} fallback="Could not waive." compact />
        </div>
      ) : null}
    </div>
  );
}

function Tally({ label, value, tone }: { label: string; value: number; tone: 'green' | 'yellow' | 'gray' }): JSX.Element {
  const cls = tone === 'green' ? 'text-green-400' : tone === 'yellow' ? 'text-yellow-300' : 'text-gray-400';
  return (
    <div className="rounded border border-gray-900 p-4 text-center">
      <p className={`text-2xl ${cls}`}>{value}</p>
      <p className="text-[0.65rem] uppercase tracking-wide text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function AvgTable({ title, rows }: { title: string; rows: Array<{ label: string; count: number; avgFinalScore: number }> }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-4 space-y-2">
      <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
      {rows.length === 0 ? <Muted>—</Muted> : rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between text-sm">
          <span className="text-gray-300 truncate">{r.label}</span>
          <span className="text-white font-mono">{r.avgFinalScore} <span className="text-gray-600 text-xs">({r.count})</span></span>
        </div>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-gray-500">{children}</p>;
}

const inputCls = 'rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';
const primaryBtn = 'rounded bg-green-700 text-white text-xs px-3 py-1.5 disabled:opacity-50';
const secondaryBtn = 'rounded border border-gray-700 text-xs px-3 py-1.5 hover:bg-gray-900';
